import type { Feature } from "geojson";
import type { MapMouseEvent } from "maplibre-gl";

import type { PopupMenuFunction } from "@/features/app/PopupMenuProvider";
import { deepMergeKeepArrays } from "@/features/editor/components/overlay_layers/utils";
import type { EventCallback } from "@/features/map/hooks/events/types";
import { uuidv4 } from "@/features/utils/utils";

export type GeojsonEditorDelta = {
  updated?: Record<string, Feature>;
  added?: Feature[];
  deleted?: Feature["id"][];
};

type EditorModeEventHandlers = {
  onVertexDrag?: EventCallback;
  onVertexDragEnd?: EventCallback;
  onVertexClick?: EventCallback;
  onVertexRightClick?: EventCallback;
  onVertexHover?: EventCallback;
  onVertexHoverEnd?: EventCallback;
  onMidPointClick?: EventCallback;
  onLineDrag?: EventCallback;
  onFillDrag?: EventCallback;
  onFillClick?: EventCallback;
  onFillRightClick?: EventCallback;
  onMapClick?: EventCallback;
  onMapRightClick?: EventCallback;
  onMouseMove?: (e: MapMouseEvent) => void;
  onCancel?: () => void;
  onDelete?: () => void;
  onEnter?: () => void;
  onControl?: () => void;
};

export type ChangeCallback = (
  data: GeojsonEditorDelta,
  notifyListeners: boolean,
) => void;

export class BaseMode {
  eventHandlers: EditorModeEventHandlers = {};
  _features: Record<string, Feature> = {};
  protected _cachedMetaFeatures: Feature[] = [];
  private _lastFeatureHash: string = "";

  constructor(
    protected onChange: ChangeCallback,
    protected _openPopupMenu: PopupMenuFunction,
    protected changeCursor: (cursor: string) => void,
  ) {
    this.init();
  }

  /**
   * Init function to be overridden by child classes.
   */
  protected init() {}

  /**
   * Destroy function to be overridden by child classes.
   */
  public destroy() {}

  /**
   * Create a hash of the features.
   */
  private createFeaturesHash(): string {
    return (
      Object.keys(this._features).sort().join(",") +
      Object.values(this._features)
        .map((f) => JSON.stringify(f.geometry))
        .join(",")
    );
  }

  /**
   * Check if the meta features should be recalculated.
   */
  protected shouldRecalculateMetaFeatures(): boolean {
    const currentHash = this.createFeaturesHash();
    if (
      currentHash === this._lastFeatureHash &&
      Object.keys(this._features).length > 0
    ) {
      return false;
    }
    this._lastFeatureHash = currentHash;
    return true;
  }

  /**
   * Update the features list of the current mode.
   */
  setFeatures(fs: Record<string, Feature>) {
    this._features = fs;
  }

  /**
   * Generate a unique id for a feature.
   * If the id already exists, generate a new one.
   */
  generateUniqueId(): string {
    let newId = uuidv4();
    while (this._features[newId]) {
      newId = uuidv4();
    }
    return newId;
  }

  /**
   * Apply a delta with updates to the geojson features for the current mode.
   */
  applyDelta(delta: GeojsonEditorDelta) {
    const updatedFeatures = { ...this._features };
    Object.entries(delta.updated ?? {}).forEach(([id, feature]) => {
      updatedFeatures[id] = deepMergeKeepArrays(this._features[id], feature);
    });
    delta.added?.forEach((feature) => {
      updatedFeatures[feature.id] = feature;
    });
    delta.deleted?.forEach((id) => {
      delete updatedFeatures[id];
    });
    this._features = updatedFeatures;
    // this.forceReRender();
  }

  /**
   * Force a re-render of geojson editor layer.
   */
  forceReRender() {
    this.onChange({}, false);
  }

  /**
   * Generate meta features for the current mode.
   */
  generateMetaFeatures(): Feature[] {
    throw new Error(
      "Method 'generateMetaFeatures' must be overriden in custom editor modes",
    );
  }
}
